import { useState } from 'react';
import { ActionButton, toast } from '@components';
import { useRecoilState } from 'recoil';
import { RateCard as RateCardState } from '@state';
import { useUpdateCPIIncrease } from '@api/RateCards/cpiIncreaseApis.ts';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { TUpdateCPIIncreaseRequest } from '@types';
import { CPIIncreaseScheduleDialog } from '@components/PAGE_LAYOUTS/RC_RateCard/CPIIncreaseScheduleDialog.tsx';
import { useRefetchLane } from '@hooks';

export const CPIIncreaseEditButton = ({ isDisabled }: {isDisabled: boolean}) => {
	const [open, setOpen] = useState(false);
	const [rateCard, setRateCard] = useRecoilState(RateCardState);
	const cpiIncrease = rateCard?.latest_cpi_increase;
	const { callUpdateCPIIncrease, isLoading } = useUpdateCPIIncrease();
	const { refetchLane } = useRefetchLane();

	if (!rateCard?.latest_cpi_increase || !cpiIncrease) {
		return null;
	}

	const onSubmit = async (request: TUpdateCPIIncreaseRequest) => {
		const laneExternalId = rateCard?.external_id;
		await callUpdateCPIIncrease({
			routes: [cpiIncrease.id],
			payload: {
				scheduled_at: request.scheduled_at,
				cpi_percentage: request.cpi_percentage
			}
		}).then((response) => {
			toast.success('CPI increase updated successfully.');
			const updatedCpiIncrease = response.data?.data;
			if (updatedCpiIncrease?.processed_at && laneExternalId) {
				// An increase with today's date applies immediately. Refetch lane to show updated rates.
				refetchLane(laneExternalId);
			} else {
				setRateCard((prev) => prev ? {
					...prev,
					latest_cpi_increase: updatedCpiIncrease ?? {
						...cpiIncrease,
						scheduled_at: request.scheduled_at,
						cpi_percentage: request.cpi_percentage,
					},
				} : prev);
			}
			setOpen(false);
		}).catch((err: Error) => {
			const errorData = (err.cause as any)?.response?.data;
			toast.error(errorData?.message || err.message || 'An error occurred while updating the CPI increase.');
		});
	};
	return (
		<>
			<ActionButton
				text={'Edit CPI increase'}
				Icon={EditOutlinedIcon}
				iconOnly
				iconActiveColor={'var(--neutral-06)'}
				onClick={() => setOpen(true)}
				disabled={isDisabled}
			/>
			<CPIIncreaseScheduleDialog
				cpiIncrease={cpiIncrease}
				type={'edit'}
				isOpen={open}
				onClose={() => setOpen(false)}
				isLoading={isLoading}
				onSubmit={onSubmit}
			/>
		</>
	);
};
